import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'

import {
  BookingReducerState,
  BookingStep,
  BookingWriteKind,
  GuestUpsellSelection,
} from '../types'
import { HoldLostErrorCodes } from '../constants'
import getReservationErrorCode from '../utils/getReservationErrorCode'

import bookingInitialState from './bookingInitialState'
import {
  confirmBooking,
  createBookingHold,
  fetchBookingAvailability,
  fetchBookingWidget,
  fetchMyVisits,
  joinBookingWaitlist,
  searchTableFinder,
  submitBookingDetails,
} from './bookingThunks'

/** A type with packages can't be held without one, so its first is preselected */
function getDefaultPackageId(state: BookingReducerState) {
  const bookingType = state.widget?.bookingTypes.find(
    type => type.id === state.bookingTypeId,
  )

  return bookingType?.packages?.[0]?.id ?? null
}

function clearFromHold(state: BookingReducerState) {
  state.reservation = null
  state.writeError = null
  state.idempotencyKeys = {}
}

/** A changed choice before the time invalidates everything from the hold on */
function clearFromAvailability(state: BookingReducerState) {
  state.availability = null
  state.availabilityError = null
  state.availabilityRequestId = null
  state.selectedStartsAt = null
  state.waitlistEntry = null
  clearFromHold(state)
}

const bookingSlice = createSlice({
  name: 'booking',
  initialState: bookingInitialState,
  reducers: {
    bookingStart(
      state,
      action: PayloadAction<{
        bookingTypeId: string | null
        partySize: number
        businessDate: string | null
        startsAt: string | null
      }>,
    ) {
      const { bookingTypeId, partySize, businessDate, startsAt } =
        action.payload
      const types = state.widget?.bookingTypes ?? []
      const resolvedTypeId =
        types.find(type => type.id === bookingTypeId)?.id ??
        (types.length === 1 ? types[0].id : null)

      state.bookingTypeId = resolvedTypeId
      state.packageId = getDefaultPackageId(state)
      state.partySize = partySize
      state.businessDate = businessDate
      state.upsellSelections = []
      state.specialRequest = ''
      state.isCompanyBooking = false
      state.holdLost = false
      clearFromAvailability(state)
      state.selectedStartsAt = startsAt

      if (!resolvedTypeId) {
        state.step = BookingStep.Type
      } else if (businessDate) {
        state.step = BookingStep.Time
      } else {
        state.step = BookingStep.Party
      }
    },
    bookingSetStep(state, action: PayloadAction<BookingStep>) {
      state.step = action.payload
      state.writeError = null
    },
    bookingSelectType(state, action: PayloadAction<string>) {
      state.bookingTypeId = action.payload
      state.packageId = getDefaultPackageId(state)
      state.upsellSelections = []
      clearFromAvailability(state)
      state.step = BookingStep.Party
    },
    bookingSelectPackage(state, action: PayloadAction<string>) {
      state.packageId = action.payload
      clearFromAvailability(state)
    },
    bookingSetPartySize(state, action: PayloadAction<number>) {
      state.partySize = action.payload
      clearFromAvailability(state)
    },
    bookingSetDate(state, action: PayloadAction<string>) {
      state.businessDate = action.payload
      clearFromAvailability(state)
    },
    bookingSelectTime(state, action: PayloadAction<string>) {
      if (state.selectedStartsAt !== action.payload) {
        state.selectedStartsAt = action.payload
        clearFromHold(state)
      }
      state.holdLost = false
    },
    bookingSetUpsellQuantity(
      state,
      action: PayloadAction<GuestUpsellSelection>,
    ) {
      const others = state.upsellSelections.filter(
        ({ upsellId }) => upsellId !== action.payload.upsellId,
      )

      state.upsellSelections = [...others, action.payload]
      delete state.idempotencyKeys[BookingWriteKind.Details]
    },
    bookingSetSpecialRequest(state, action: PayloadAction<string>) {
      state.specialRequest = action.payload
      delete state.idempotencyKeys[BookingWriteKind.Details]
    },
    bookingSetIsCompanyBooking(state, action: PayloadAction<boolean>) {
      state.isCompanyBooking = action.payload
      delete state.idempotencyKeys[BookingWriteKind.Details]
    },
    bookingSetIdempotencyKey(
      state,
      action: PayloadAction<{ kind: BookingWriteKind; key: string }>,
    ) {
      state.idempotencyKeys[action.payload.kind] = action.payload.key
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchTableFinder.pending, (state, action) => {
        state.finderParams = action.meta.arg
        state.finderFetchPending = true
        state.finderError = null
        state.finderRequestId = action.meta.requestId
      })
      .addCase(searchTableFinder.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.finderRequestId) {
          return
        }

        state.finderResults = action.payload
        state.finderFetchPending = false
      })
      .addCase(searchTableFinder.rejected, (state, action) => {
        if (action.meta.requestId !== state.finderRequestId) {
          return
        }

        state.finderResults = []
        state.finderFetchPending = false
        state.finderError = action.error
      })

      .addCase(fetchBookingWidget.pending, (state, action) => {
        if (state.widget?.widgetKey !== action.meta.arg.widgetKey) {
          state.widget = null
        }
        state.widgetFetchPending = true
        state.widgetError = null
      })
      .addCase(fetchBookingWidget.fulfilled, (state, action) => {
        state.widget = action.payload
        state.widgetFetchPending = false
      })
      .addCase(fetchBookingWidget.rejected, (state, action) => {
        state.widgetFetchPending = false
        state.widgetError = action.error
      })

      .addCase(fetchBookingAvailability.pending, (state, action) => {
        state.availabilityFetchPending = true
        state.availabilityError = null
        state.availabilityRequestId = action.meta.requestId
      })
      .addCase(fetchBookingAvailability.fulfilled, (state, action) => {
        // Asked for other choices than the guest has now
        if (action.meta.requestId !== state.availabilityRequestId) {
          return
        }

        state.availability = action.payload
        state.availabilityFetchPending = false

        // A chip's time that's no longer offered is dropped, not kept selected
        if (
          state.selectedStartsAt &&
          !action.payload.seatings.some(
            ({ startsAt, inquiry }) =>
              !inquiry &&
              new Date(startsAt).getTime() ===
                new Date(state.selectedStartsAt!).getTime(),
          )
        ) {
          state.selectedStartsAt = null
        }
      })
      .addCase(fetchBookingAvailability.rejected, (state, action) => {
        if (action.meta.requestId !== state.availabilityRequestId) {
          return
        }

        state.availabilityFetchPending = false
        state.availabilityError = action.error
      })

      .addCase(createBookingHold.fulfilled, (state, action) => {
        state.reservation = action.payload
        delete state.idempotencyKeys[BookingWriteKind.Hold]
        state.step = BookingStep.Details
      })
      .addCase(submitBookingDetails.fulfilled, (state, action) => {
        state.reservation = action.payload
        delete state.idempotencyKeys[BookingWriteKind.Details]
        state.step = BookingStep.Confirm
      })
      .addCase(confirmBooking.fulfilled, (state, action) => {
        state.reservation = action.payload
        delete state.idempotencyKeys[BookingWriteKind.Confirm]
        state.step = BookingStep.Confirmed
      })
      .addCase(joinBookingWaitlist.fulfilled, (state, action) => {
        state.waitlistEntry = action.payload.entry
        delete state.idempotencyKeys[BookingWriteKind.Waitlist]
      })

      .addCase(fetchMyVisits.pending, state => {
        state.visitsFetchPending = true
      })
      .addCase(fetchMyVisits.fulfilled, (state, action) => {
        state.visits = action.payload
        state.visitsFetchPending = false
      })
      .addCase(fetchMyVisits.rejected, state => {
        state.visitsFetchPending = false
      })

      .addMatcher(
        isAnyOf(
          createBookingHold.pending,
          submitBookingDetails.pending,
          confirmBooking.pending,
          joinBookingWaitlist.pending,
        ),
        (state, action) => {
          const kinds: Record<string, BookingWriteKind> = {
            [createBookingHold.pending.type]: BookingWriteKind.Hold,
            [submitBookingDetails.pending.type]: BookingWriteKind.Details,
            [confirmBooking.pending.type]: BookingWriteKind.Confirm,
            [joinBookingWaitlist.pending.type]: BookingWriteKind.Waitlist,
          }

          state.writePending = kinds[action.type]
          state.writeError = null
        },
      )
      .addMatcher(
        isAnyOf(
          createBookingHold.fulfilled,
          submitBookingDetails.fulfilled,
          confirmBooking.fulfilled,
          joinBookingWaitlist.fulfilled,
        ),
        state => {
          state.writePending = null
        },
      )
      .addMatcher(
        isAnyOf(
          createBookingHold.rejected,
          submitBookingDetails.rejected,
          confirmBooking.rejected,
          joinBookingWaitlist.rejected,
        ),
        (state, action) => {
          const error = (action.payload ?? action.error) as typeof action.error
          const errorCode = getReservationErrorCode(error)

          // A write the service turned down was not stored, so the corrected
          // retry gets a new key; a timeout or 5xx keeps it for a safe resend
          if (errorCode && state.writePending) {
            delete state.idempotencyKeys[state.writePending]
          }

          state.writePending = null

          // The seating went or the hold ran out: back to the times
          if (errorCode && HoldLostErrorCodes.includes(errorCode)) {
            state.holdLost = true
            state.selectedStartsAt = null
            clearFromHold(state)
            state.step = BookingStep.Time
            return
          }

          state.writeError = error
        },
      )
  },
})

export const {
  bookingStart,
  bookingSetStep,
  bookingSelectType,
  bookingSelectPackage,
  bookingSetPartySize,
  bookingSetDate,
  bookingSelectTime,
  bookingSetUpsellQuantity,
  bookingSetSpecialRequest,
  bookingSetIsCompanyBooking,
  bookingSetIdempotencyKey,
} = bookingSlice.actions

export default bookingSlice.reducer
